import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * Buy-N-pick-N liter gift picker. Same mechanic as
 * CartBogoRoutinesComponent (cart-bogo-routines.js) — see that file's
 * comments for the full rationale, including its module-level
 * reconciler — applied here to litres' own _bogo_litres_gift property
 * and independent entitlement pool. Deliberately a straight copy rather
 * than a shared import: routines and litres must stay fully independent
 * promos (buying a coffret never funds a free liter pick and vice
 * versa), matching grid-2027's own file-for-file duplication of this
 * pattern — see .claude/rules/cart-promotions.md "BOGO litres".
 *
 * @extends {Component}
 */
class CartBogoLitresComponent extends Component {
  /** @type {boolean} */
  #isUpdating = false;

  /**
   * Bound to both on:click and on:keydown (single-pick mode cards use
   * role="button" tabindex="0", not a native <button>, so Enter/Space
   * activation has to be handled explicitly — same convention as
   * assets/facets.js's removeFilter()).
   *
   * @param {{ variantId: string }} data
   * @param {Event} event
   */
  handleSelect = async (data, event) => {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    if (!data?.variantId || this.#isUpdating) return;
    if (!acquireGiftLock()) return;
    this.#isUpdating = true;
    const card = event.currentTarget instanceof Element ? event.currentTarget.closest('[data-variant-id]') : null;
    this.#setCardBusy(card, true);
    try {
      await this.#syncPick(String(data.variantId), 1, { replace: true });
    } finally {
      this.#isUpdating = false;
      this.#setCardBusy(card, false);
      releaseGiftLock();
    }
  };

  /**
   * @param {{ variantId: string }} data
   * @param {Event} event
   */
  handleIncrement = async (data, event) => {
    if (!data?.variantId || this.#isUpdating) return;
    const entitlement = parseInt(this.dataset.entitlement || '0', 10);
    const pickedTotal = await this.#currentPickedTotal();
    if (pickedTotal >= entitlement) return;
    if (!acquireGiftLock()) return;
    this.#isUpdating = true;
    const card = event?.currentTarget instanceof Element ? event.currentTarget.closest('[data-variant-id]') : null;
    this.#setCardBusy(card, true);
    try {
      await this.#syncPick(String(data.variantId), 1, { delta: true });
    } finally {
      this.#isUpdating = false;
      this.#setCardBusy(card, false);
      releaseGiftLock();
    }
  };

  /**
   * @param {{ variantId: string }} data
   * @param {Event} event
   */
  handleDecrement = async (data, event) => {
    if (!data?.variantId || this.#isUpdating) return;
    if (!acquireGiftLock()) return;
    this.#isUpdating = true;
    const card = event?.currentTarget instanceof Element ? event.currentTarget.closest('[data-variant-id]') : null;
    this.#setCardBusy(card, true);
    try {
      await this.#syncPick(String(data.variantId), -1, { delta: true });
    } finally {
      this.#isUpdating = false;
      this.#setCardBusy(card, false);
      releaseGiftLock();
    }
  };

  /**
   * Toggles the visible spinner overlay (.cart-bogo-routines__card-loading,
   * shared class names with cart-bogo-routines.js since litres cards reuse
   * that CSS — see cart-bogo-litres-card.liquid) on the card currently
   * being changed. Purely presentational; safe to call with a stale/
   * detached `card` reference.
   * @param {Element|null} card
   * @param {boolean} busy
   */
  #setCardBusy(card, busy) {
    if (!card) return;
    card.classList.toggle('is-loading', busy);
  }

  /**
   * Reads the live cart and sums _bogo_litres_gift quantities.
   * @returns {Promise<number>}
   */
  async #currentPickedTotal() {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    return cart.items
      .filter((item) => item.properties && item.properties['_bogo_litres_gift'] === 'true')
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Finds the existing cart line (if any) for a picked variant.
   * @param {string} variantId
   * @returns {Promise<{ line: object|null }>}
   */
  async #findExistingLine(variantId) {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    const line = cart.items.find(
      (item) =>
        String(item.variant_id) === variantId &&
        item.properties &&
        item.properties['_bogo_litres_gift'] === 'true',
    );
    return { line: line || null };
  }

  /**
   * Adds, increments, decrements, or removes a picked variant's gift
   * line depending on mode. Caller owns acquiring/releasing the shared
   * gift lock and the #isUpdating guard.
   * @param {string} variantId
   * @param {number} amount - +1 to add/increment, -1 to decrement
   * @param {{ replace?: boolean, delta?: boolean }} mode
   */
  async #syncPick(variantId, amount, mode) {
    const { line } = await this.#findExistingLine(variantId);

    if (mode.replace) {
      // Single-pick mode: remove any other litres pick, then ensure
      // this one exists at quantity 1.
      await this.#removeAllOtherPicks(variantId);
      if (!line) {
        await this.#addGiftLine(variantId, 1);
      }
      return;
    }

    // Stepper mode.
    if (amount > 0) {
      if (line) {
        await this.#changeLineQuantity(line.key, line.quantity + 1);
      } else {
        await this.#addGiftLine(variantId, 1);
      }
    } else if (line) {
      const nextQty = line.quantity - 1;
      await this.#changeLineQuantity(line.key, Math.max(nextQty, 0));
    }
  }

  /**
   * Removes every _bogo_litres_gift line except the one matching
   * variantId (used by single-pick mode to swap the pick in one action).
   * @param {string} keepVariantId
   */
  async #removeAllOtherPicks(keepVariantId) {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    const others = cart.items.filter(
      (item) =>
        item.properties &&
        item.properties['_bogo_litres_gift'] === 'true' &&
        String(item.variant_id) !== keepVariantId,
    );
    for (const item of others) {
      await this.#changeLineQuantity(item.key, 0);
    }
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   */
  async #addGiftLine(variantId, quantity) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', String(quantity));
    formData.append('properties[_bogo_litres_gift]', 'true');
    formData.append('properties[_free_gift]', 'true');
    formData.append('sections', this.dataset.sectionId || '');
    await this.#post('/cart/add.js', formData);
  }

  /**
   * @param {string} lineKey
   * @param {number} quantity
   */
  async #changeLineQuantity(lineKey, quantity) {
    const formData = new FormData();
    formData.append('id', lineKey);
    formData.append('quantity', String(quantity));
    formData.append('sections', this.dataset.sectionId || '');
    await this.#post('/cart/change.js', formData);
  }

  /**
   * @param {string} url
   * @param {FormData} formData
   */
  async #post(url, formData) {
    await postAndSync(url, formData, this.dataset.sectionId, this.id);
  }
}

if (!customElements.get('cart-bogo-litres-component')) {
  customElements.define('cart-bogo-litres-component', CartBogoLitresComponent);
}

/* ── Shared cross-promo mutex ──────────────────────────────────────────────
 * Own copy of the same LOCK_OWNER pattern as assets/cart-bogo-routines.js
 * — see that file's comment. Different LOCK_OWNER string so a debug read
 * of window.__lvGiftLock during a stuck lock reports which promo holds it.
 */
const LOCK_OWNER = 'bogo-litres';
const LOCK_TTL_MS = 10000;

function acquireGiftLock() {
  const held = window.__lvGiftLock;
  if (held && Date.now() - held.at < LOCK_TTL_MS) return false;
  window.__lvGiftLock = { owner: LOCK_OWNER, at: Date.now() };
  return true;
}

function releaseGiftLock() {
  const held = window.__lvGiftLock;
  if (held && held.owner === LOCK_OWNER) window.__lvGiftLock = null;
}

/**
 * POSTs a cart mutation, morphs the affected section from the response's
 * own sections payload, and dispatches CartUpdateEvent. See
 * assets/cart-bogo-routines.js's own postAndSync for the full reasoning.
 * @param {string} url
 * @param {FormData} formData
 * @param {string} [sectionId]
 * @param {string} [sourceId]
 */
async function postAndSync(url, formData, sectionId, sourceId) {
  const response = await fetch(url, fetchConfig('javascript', { body: formData }));
  if (!response.ok) throw new Error(`Cart BOGO litres request failed: ${url}`);

  const data = await response.json();
  if (data.sections && sectionId && data.sections[sectionId]) {
    await morphSection(sectionId, data.sections[sectionId]);
  }

  document.dispatchEvent(
    new CartUpdateEvent(data, sourceId || 'cart-bogo-litres-reconciler', {
      source: 'cart-bogo-litres',
      itemCount: data.item_count,
    }),
  );
}

/* ── Reconciler ──────────────────────────────────────────────────────────
 * Same trim/top-up rules as assets/cart-bogo-routines.js's own reconciler
 * — see that file's long comment for the full rationale. Reads its own
 * independent entitlement pool from _bogo_litres_gift lines and the
 * litres trigger collection's variant ids only, so it can never be
 * influenced by (or influence) the routines reconciler's own pool.
 */
/* Wrapped in an initializer that waits for the config script via
 * MutationObserver — see cart-bogo-routines.js's identical fix and
 * comment for the full reasoning. Root cause, confirmed live via direct
 * diagnostic logging: <div data-gift-sections hidden> in
 * cart-drawer.liquid only renders inside the {% else %} branch of
 * {% if cart.empty? %} — on a fresh page load with an EMPTY cart this
 * config script is genuinely absent from the server-rendered HTML, full
 * stop. It only starts existing once the drawer's section is morphed
 * with a non-empty cart, i.e. the moment the customer's first
 * add-to-cart completes — timing that depends on network conditions and
 * isn't bounded by any fixed retry window. A capped retry (5 attempts
 * over 2 seconds, tried first) raced against that morph and lost
 * unpredictably, which is exactly what read as the default gift
 * "randomly" not appearing. */
function initReconciler() {
  const configEl = document.querySelector('[data-bogo-litres-config]');

  if (!configEl) {
    const observer = new MutationObserver(() => {
      const el = document.querySelector('[data-bogo-litres-config]');
      if (el) {
        observer.disconnect();
        initReconciler();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return;
  }

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    config = null;
  }

  if (config && config.enabled) {
    const triggerVariantIds = {};
    (config.triggerVariantIds || []).forEach((id) => {
      triggerVariantIds[String(id)] = true;
    });

    if (Object.keys(triggerVariantIds).length > 0) {
      /* Retry-counted, not a permanent one-shot blacklist — see
       * cart-bogo-routines.js's identical fix and comment. */
      const unavailableAttempts = {}; // variantId -> consecutive 422 count this session
      const MAX_UNAVAILABLE_ATTEMPTS = 3;
      let suppress = false;
      let isUpdating = false;
      let debounceTimer = null;
      let lastKnownBoughtQty = null; // null until the first reconcile pass has run

      const getSectionId = () => {
        const el = document.querySelector('[data-bogo-litres-config]');
        return el ? el.dataset.sectionId : configEl.dataset.sectionId;
      };

      function refresh() {
        suppress = true;
        setTimeout(() => {
          suppress = false;
        }, 800);
      }

      /* async + try/finally — see cart-bogo-routines.js's own comment on
       * this exact same fix. A chained .then/.catch/.then left isUpdating
       * permanently stuck at true if anything between setting it and the
       * first .then() threw synchronously, wedging the whole reconciler
       * (top-up AND removal both gated on this one flag) — a real,
       * confirmed live report ("le cadeau sans discount n'est pas
       * enlevé"). try/finally guarantees cleanup regardless. */
      async function changeQty(item, qty) {
        if (isUpdating) return;
        isUpdating = true;
        try {
          const formData = new FormData();
          formData.append('id', item.key);
          formData.append('quantity', String(qty));
          formData.append('sections', getSectionId() || '');
          await postAndSync('/cart/change.js', formData, getSectionId());
          refresh();
        } catch (e) {
          console.error('[BOGO Litres] Reconcile failed:', e);
        } finally {
          isUpdating = false;
        }
      }

      /**
       * Only advances lastKnownBoughtQty on a CONFIRMED successful add —
       * see reconcile()'s own comment and cart-bogo-routines.js's
       * identical fix. Advancing it regardless of outcome was the second
       * half of the stuck-reconciler bug: a top-up that silently no-op'd
       * still marked that entitlement "seen," permanently skipping the
       * retry every later pass would otherwise have attempted.
       * @param {string} variantId
       * @param {number} qty
       */
      async function addDefaultGift(variantId, qty) {
        if (isUpdating || (unavailableAttempts[variantId] || 0) >= MAX_UNAVAILABLE_ATTEMPTS) return;
        if (!acquireGiftLock()) return;
        isUpdating = true;
        try {
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', String(qty));
          formData.append('properties[_bogo_litres_gift]', 'true');
          formData.append('properties[_free_gift]', 'true');
          formData.append('sections', getSectionId() || '');

          const res = await fetch('/cart/add.js', fetchConfig('javascript', { body: formData }));
          if (res.status === 422) {
            unavailableAttempts[variantId] = (unavailableAttempts[variantId] || 0) + 1;
            return;
          }
          if (!res.ok) throw new Error(String(res.status));

          const data = await res.json();
          const sectionId = getSectionId();
          if (data.sections && sectionId && data.sections[sectionId]) {
            await morphSection(sectionId, data.sections[sectionId]);
            document.dispatchEvent(
              new CartUpdateEvent(data, 'cart-bogo-litres-reconciler', {
                source: 'cart-bogo-litres',
                itemCount: data.item_count,
              }),
            );
          }
          refresh();
          lastKnownBoughtQty = (lastKnownBoughtQty || 0) + qty;
          unavailableAttempts[variantId] = 0;
        } catch (e) {
          console.error('[BOGO Litres] Default add failed:', e);
        } finally {
          isUpdating = false;
          releaseGiftLock();
        }
      }

      function reconcile() {
        if (suppress || isUpdating) return;

        fetch('/cart.js')
          .then((res) => (res.ok ? res.json() : null))
          .then((cart) => {
            if (!cart) return;

            let boughtQty = 0;
            let lastTriggerVariantId = null;
            const giftItems = [];

            (cart.items || []).forEach((item) => {
              const p = item.properties || {};
              if (p._bogo_litres_gift === 'true') {
                giftItems.push(item);
                return;
              }
              // A gift line never earns another gift.
              if (p._free_gift === 'true' || p._tier_gift === 'true') return;
              if (triggerVariantIds[String(item.variant_id)]) {
                boughtQty += item.quantity;
                lastTriggerVariantId = String(item.variant_id);
              }
            });

            let pickedTotal = 0;
            giftItems.forEach((item) => {
              pickedTotal += item.quantity;
            });

            if (boughtQty === 0) {
              // Trigger no longer met at all — drop every pick.
              lastKnownBoughtQty = 0;
              const overage = giftItems.find((item) => item.quantity > 0);
              if (overage) changeQty(overage, 0);
              return;
            }

            if (pickedTotal > boughtQty) {
              const priciest = giftItems.reduce(
                (max, item) => (!max || item.price > max.price ? item : max),
                null,
              );
              if (priciest) changeQty(priciest, priciest.quantity - 1);
              return;
            }

            if (lastKnownBoughtQty === null) lastKnownBoughtQty = 0;
            const newEntitlement = boughtQty - lastKnownBoughtQty;
            if (newEntitlement > 0 && pickedTotal < boughtQty && lastTriggerVariantId) {
              const shortfall = boughtQty - pickedTotal;
              const topUp = Math.min(newEntitlement, shortfall);
              if (topUp > 0) {
                // addDefaultGift owns advancing lastKnownBoughtQty itself,
                // only on confirmed success — see its own comment.
                addDefaultGift(lastTriggerVariantId, topUp);
                return;
              }
            }
            lastKnownBoughtQty = boughtQty;
          })
          .catch((e) => console.error('[BOGO Litres]', e));
      }

      document.addEventListener('cart:update', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(reconcile, 200);
      });

      setTimeout(reconcile, 600);

      /* Periodic safety net — see cart-bogo-routines.js's identical fix
       * and comment for the full reasoning. */
      setInterval(reconcile, 3000);
    }
  }
}

try {
  initReconciler();
} catch (e) {
  console.error('[BOGO Litres] initReconciler threw:', e);
}
